import { cloneDeep } from "lodash-es";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useCompaniesStore } from "@/pinia/companiesStore";

/**
 * User Settings Mixin
 *
 * Stores user preferences (filters, sort order, date ranges, toggles) with company-scoped isolation.
 *
 * Storage strategy:
 * - Viewing OWN company: Saves to backend DB + localStorage (syncs across devices)
 * - Viewing OTHER company: Saves to localStorage only (isolated, temporary state)
 *
 * localStorage key format: company_filters_company:{companyId}_app:{appId}
 *
 * Benefits:
 * - Own company preferences sync across devices (DB backend)
 * - Other company preferences are isolated per-company (no collision)
 * - Fast local access via localStorage
 * - Backward compatible with existing DB settings
 */
export default {
  computed: {
    userStore: () => useUserStore(),
    appsStore: () => useAppsStore(),
    companiesStore: () => useCompaniesStore(),
    user() {
      return this.userStore?.currUser;
    },
    app() {
      return this.appsStore?.app;
    },
    /**
     * Get the current company identifier based on context:
     * - If viewing another company, use viewingCompanySlug
     * - Otherwise, use the user's own company_id
     */
    currentCompanyIdentifier() {
      const viewingSlug = this.companiesStore?.viewingCompanySlug;
      return viewingSlug || this.user?.company_id || "";
    },
    /**
     * Check if user is viewing their own company (not switched to another)
     */
    isViewingOwnCompany() {
      return !this.companiesStore?.viewingCompanySlug;
    },
    /**
     * Generate localStorage key for current company + app context
     */
    storageKey() {
      const companyId = this.currentCompanyIdentifier;
      const appId = this.app?.id;

      if (!companyId || !appId) {
        return null;
      }
      return `company_filters_company:${companyId}_app:${appId}`;
    },
    /**
     * All settings for current company + app
     * - First checks localStorage (company-scoped)
     * - Falls back to backend settings (from user.settings)
     */
    allItems() {
      if (!this.storageKey) {
        return {};
      }

      try {
        // Try to read from localStorage first (company-scoped approach)
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          return JSON.parse(stored);
        }

        // Fallback: read from backend settings
        // Used when viewing own company and localStorage is empty
        const appId = this.app?.id;
        if (appId && this.user?.settings?.[appId]?.rf_filter_settings) {
          return this.user.settings[appId].rf_filter_settings;
        }

        return {};
      } catch (e) {
        console.error("Error reading settings:", e);
        return {};
      }
    },
  },
  methods: {
    /**
     * Get a single setting value by key
     * @param {string} key - The setting key (e.g., 'promptsStatusFilter')
     * @returns {*} The setting value, or undefined if not found
     */
    getUserSetting(key) {
      return this.allItems[key];
    },
    /**
     * Set a single setting value by key
     * @param {string} key - The setting key
     * @param {*} value - The setting value
     * @returns {Promise<boolean>} Promise that resolves when saved
     */
    setUserSetting(key, value) {
      const items = cloneDeep(this.allItems);

      if (this.valuesDifferent(items[key], value)) {
        items[key] = value;
        return this.updateUserSettings(items);
      }
      return Promise.resolve(true);
    },
    /**
     * Set multiple settings at once
     * @param {Object} settings - Object with setting key-value pairs
     * @returns {Promise<boolean>|undefined} Promise that resolves when saved, or undefined if no changes
     */
    setUserSettings(settings) {
      const items = cloneDeep(this.allItems);
      let changed = false;

      Object.keys(settings).forEach(key => {
        const value = settings[key];
        if (this.valuesDifferent(items[key], value)) {
          items[key] = value;
          changed = true;
        }
      });

      if (changed) {
        return this.updateUserSettings(items);
      }
    },
    /**
     * Save settings to appropriate storage:
     * - Own company: Save to backend DB (syncs across devices)
     * - Viewing other company: Save to localStorage only (isolated state)
     * @param {Object} items - All settings to save
     * @returns {Promise<boolean>} Promise that resolves to true when saved
     * @private
     */
    updateUserSettings(items) {
      if (!this.storageKey) {
        // Silently skip if data not loaded yet (happens during initial mount)
        // This is OK - saves will work once user/app data is loaded
        return Promise.resolve(false);
      }

      // Always save to localStorage for fast local access
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(items));
      } catch (e) {
        console.error("Error saving settings to localStorage:", e);
      }

      // If viewing own company, also save to backend DB
      if (this.isViewingOwnCompany && this.user && this.app?.id) {
        try {
          const appSettings = { rf_filter_settings: items };
          const user = cloneDeep(this.user);
          if (!user.settings) user.settings = {};
          user.settings[this.app.id] = appSettings;

          return this.userStore.updateUserSettings(user);
        } catch (e) {
          console.error("Error saving settings to backend:", e);
          return Promise.resolve(false);
        }
      }

      return Promise.resolve(true);
    },
    /**
     * Check if two values are different
     * Handles arrays and primitives
     * @param {*} val1 - First value
     * @param {*} val2 - Second value
     * @returns {boolean} True if values are different
     * @private
     */
    valuesDifferent(val1, val2) {
      if (Array.isArray(val1) && Array.isArray(val2)) {
        if (val1.length !== val2.length) return true;
        return val1.some((element, index) => element !== val2[index]);
      }
      return val1 !== val2;
    },
  },
};
